import { useCallback, useReducer, useRef } from 'react';
import { posthog } from '../lib/posthog';
import { TimelineStep } from '../types/timeline';
import { frameToTime } from '../Viewer/animation-v2/utils';

export type StepPhase = 'idle' | 'transition' | 'playing' | 'paused';

export interface StepNavigationState {
  /** Current phase of the step state machine */
  phase: StepPhase;
  /** 0-based index into the sorted steps array */
  currentStepIndex: number;
  /** Frame the timeline should not exceed while step content is playing */
  rangeEndFrame: number | null;
  /** One-shot seek command consumed by DroneModel */
  seekCommand: { frame: number; id: number } | null;
}

type Action =
  | { type: 'GOTO_STEP'; index: number }
  | { type: 'PLAY' }
  | { type: 'PAUSE' }
  | { type: 'TRANSITION_COMPLETE' }
  | { type: 'RANGE_COMPLETE' }
  | { type: 'SEEK_CONSUMED' }
  | { type: 'ADMIN_SEEK'; frame: number };

function useSeekCounter() {
  const ref = useRef(0);
  return (frame: number) => ({ frame, id: ++ref.current });
}

function clampStepIndex(steps: TimelineStep[], index: number): number {
  if (steps.length === 0) return 0;
  return Math.max(0, Math.min(index, steps.length - 1));
}

function resolveAdminStepIndex(steps: TimelineStep[], frame: number, fallback: number): number {
  if (steps.length === 0) return fallback;
  if (frame <= steps[0].startFrame) return 0;
  if (frame >= steps[steps.length - 1].endFrame) return steps.length - 1;

  for (let i = 0; i < steps.length; i++) {
    if (frame >= steps[i].startFrame && frame <= steps[i].endFrame) {
      return i;
    }
  }

  return fallback;
}

function isAtOrPastStepEnd(
  endFrame: number,
  currentFrame: number,
  currentTimeSeconds?: number,
  fps?: number,
): boolean {
  if (currentFrame >= endFrame) return true;
  if (currentTimeSeconds === undefined || fps === undefined) return false;
  return currentTimeSeconds >= frameToTime(endFrame, fps);
}

function reducer(
  state: StepNavigationState,
  action: Action & {
    _steps?: TimelineStep[];
    _currentFrame?: number;
    _currentTimeSeconds?: number;
    _fps?: number;
    _makeSeek?: (frame: number) => { frame: number; id: number };
  },
): StepNavigationState {
  const steps = action._steps ?? [];
  const currentFrame = action._currentFrame ?? 0;
  const currentTimeSeconds = action._currentTimeSeconds;
  const fps = action._fps;

  switch (action.type) {
    case 'GOTO_STEP': {
      const nextStep = steps[action.index];
      if (!nextStep) return state;

      return {
        phase: 'transition',
        currentStepIndex: action.index,
        rangeEndFrame: null,
        seekCommand: action._makeSeek ? action._makeSeek(nextStep.startFrame) : null,
      };
    }

    case 'TRANSITION_COMPLETE': {
      const currentStep = steps[state.currentStepIndex];
      if (!currentStep) return { ...state, phase: 'idle' };

      return {
        ...state,
        phase: 'playing',
        rangeEndFrame: currentStep.endFrame,
        seekCommand: null,
      };
    }

    case 'PLAY': {
      if (state.phase === 'playing' || state.phase === 'transition') {
        return state;
      }

      if (state.phase === 'idle') {
        const firstStep = steps[0];
        if (!firstStep) return state;

        return {
          phase: 'playing',
          currentStepIndex: 0,
          rangeEndFrame: firstStep.endFrame,
          seekCommand: action._makeSeek ? action._makeSeek(firstStep.startFrame) : null,
        };
      }

      const currentStep = steps[state.currentStepIndex];
      if (!currentStep) return state;

      if (isAtOrPastStepEnd(currentStep.endFrame, currentFrame, currentTimeSeconds, fps)) {
        if (state.currentStepIndex < steps.length - 1) {
          const nextIndex = state.currentStepIndex + 1;
          const nextStep = steps[nextIndex];
          if (!nextStep) return state;

          return {
            ...state,
            phase: 'playing',
            currentStepIndex: nextIndex,
            rangeEndFrame: nextStep.endFrame,
            seekCommand: action._makeSeek ? action._makeSeek(nextStep.startFrame) : null,
          };
        }

        return {
          ...state,
          phase: 'playing',
          rangeEndFrame: currentStep.endFrame,
          seekCommand: action._makeSeek ? action._makeSeek(currentStep.startFrame) : null,
        };
      }

      return {
        ...state,
        phase: 'playing',
        rangeEndFrame: currentStep.endFrame,
        seekCommand: null,
      };
    }

    case 'PAUSE': {
      return {
        ...state,
        phase: state.phase === 'playing' || state.phase === 'transition' ? 'paused' : state.phase,
        rangeEndFrame: null,
      };
    }

    case 'RANGE_COMPLETE': {
      if (state.phase === 'transition') {
        const currentStep = steps[state.currentStepIndex];
        if (!currentStep) return { ...state, phase: 'paused' };

        return {
          ...state,
          phase: 'playing',
          rangeEndFrame: currentStep.endFrame,
        };
      }

      return {
        ...state,
        phase: 'paused',
        rangeEndFrame: null,
      };
    }

    case 'SEEK_CONSUMED': {
      return state.seekCommand ? { ...state, seekCommand: null } : state;
    }

    case 'ADMIN_SEEK': {
      return {
        ...state,
        phase: 'paused',
        currentStepIndex: resolveAdminStepIndex(steps, action.frame, state.currentStepIndex),
        rangeEndFrame: null,
        seekCommand: action._makeSeek ? action._makeSeek(action.frame) : null,
      };
    }

    default:
      return state;
  }
}

interface UseStepNavigationOptions {
  steps: TimelineStep[];
  initialStepIndex?: number;
  currentFrame?: number;
  currentTimeSeconds?: number;
  fps?: number;
}

export function useStepNavigation({
  steps,
  initialStepIndex = 0,
  currentFrame = 0,
  currentTimeSeconds,
  fps,
}: UseStepNavigationOptions) {
  const makeSeek = useSeekCounter();
  const initialState: StepNavigationState = {
    phase: 'idle',
    currentStepIndex: clampStepIndex(steps, initialStepIndex),
    rangeEndFrame: null,
    seekCommand: null,
  };

  const [state, rawDispatch] = useReducer(reducer, initialState);

  const dispatch = useCallback(
    (action: Action) =>
      rawDispatch({
        ...action,
        _steps: steps,
        _currentFrame: currentFrame,
        _currentTimeSeconds: currentTimeSeconds,
        _fps: fps,
        _makeSeek: makeSeek,
      }),
    [currentFrame, currentTimeSeconds, fps, makeSeek, steps],
  );

  const isPlaying = state.phase === 'playing' || state.phase === 'transition';
  const currentStep = steps[state.currentStepIndex] ?? null;
  const currentStepNumber = currentStep ? state.currentStepIndex + 1 : 0;

  const gotoStep = useCallback(
    (stepNumber: number) => {
      const index = Math.max(0, Math.min(stepNumber - 1, steps.length - 1));
      dispatch({ type: 'GOTO_STEP', index });
      posthog.capture('viewer_step_navigated', { step_number: stepNumber });
    },
    [dispatch, steps.length],
  );

  const next = useCallback(() => {
    if (state.phase === 'idle') {
      gotoStep(1);
      return;
    }

    if (state.currentStepIndex < steps.length - 1) {
      gotoStep(state.currentStepIndex + 2);
    }
  }, [gotoStep, state.currentStepIndex, state.phase, steps.length]);

  const previous = useCallback(() => {
    if (state.currentStepIndex > 0) {
      gotoStep(state.currentStepIndex);
    }
  }, [gotoStep, state.currentStepIndex]);

  const play = useCallback(() => {
    dispatch({ type: 'PLAY' });
    posthog.capture('viewer_playback_started');
  }, [dispatch]);
  const pause = useCallback(() => dispatch({ type: 'PAUSE' }), [dispatch]);
  const onRangeComplete = useCallback(() => dispatch({ type: 'RANGE_COMPLETE' }), [dispatch]);
  const onTransitionComplete = useCallback(() => dispatch({ type: 'TRANSITION_COMPLETE' }), [dispatch]);
  const seekToFrame = useCallback((frame: number) => dispatch({ type: 'ADMIN_SEEK', frame }), [dispatch]);

  const togglePlayPause = useCallback(() => {
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  }, [isPlaying, pause, play]);

  const consumeSeek = useCallback(() => dispatch({ type: 'SEEK_CONSUMED' }), [dispatch]);

  return {
    phase: state.phase,
    currentStepIndex: state.currentStepIndex,
    currentStepNumber,
    currentStep,
    isPlaying,
    rangeEndFrame: state.rangeEndFrame,
    seekCommand: state.seekCommand,
    next,
    previous,
    gotoStep,
    play,
    pause,
    togglePlayPause,
    seekToFrame,
    onRangeComplete,
    onTransitionComplete,
    consumeSeek,
  };
}
